import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { STRIPE_PROXY_URL } from '../../config/billing';

interface SubscriptionActionsProps {
  subscription: any;
  onUpdated?: () => void;
}

const PAUSE_REASONS = [
  'Customer requested temporary hold',
  'Payment failure / collection issue',
  'Property offline / under maintenance',
  'Billing dispute under review',
  'Awaiting contract renewal',
  'Other',
];

export const SubscriptionActions: React.FC<SubscriptionActionsProps> = ({ subscription, onUpdated }) => {
  const [pauseLoading, setPauseLoading] = useState(false);
  const [pausePromptOpen, setPausePromptOpen] = useState(false);
  const [pauseReason, setPauseReason] = useState(PAUSE_REASONS[0]);
  const [pauseReasonOther, setPauseReasonOther] = useState('');
  const [error, setError] = useState('');

  const isPaused = !!subscription.pause_collection;
  const isCanceled = subscription.status === 'canceled';

  const submitPauseResume = async (reason?: string) => {
    setPauseLoading(true);
    setError('');
    try {
      const action = isPaused ? 'resume' : 'pause';
      const res = await fetch(`${STRIPE_PROXY_URL}/subscriptions/${subscription.id}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reason ? { reason } : {}),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error || 'Failed'); }
      setPausePromptOpen(false);
      onUpdated?.();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setPauseLoading(false);
    }
  };

  const handlePauseClick = () => {
    if (isPaused) { submitPauseResume(); return; }
    setPauseReason(PAUSE_REASONS[0]);
    setPauseReasonOther('');
    setPausePromptOpen(true);
  };

  const confirmPause = () => {
    const reason = pauseReason === 'Other' ? pauseReasonOther.trim() : pauseReason;
    if (!reason) { setError('Please enter a reason'); return; }
    submitPauseResume(reason);
  };

  if (isCanceled) return <span className="text-xs text-gray-400">Canceled</span>;

  return (
    <>
      <div className="flex items-center gap-1">
        <button
          onClick={handlePauseClick}
          disabled={pauseLoading}
          className={`px-2 py-1 rounded text-xs font-medium ${
            isPaused
              ? 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
              : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border border-yellow-200'
          } ${pauseLoading ? 'opacity-50' : ''}`}
        >
          {pauseLoading ? '...' : isPaused ? 'Resume' : 'Pause'}
        </button>
      </div>
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}

      <Modal open={pausePromptOpen} onClose={() => setPausePromptOpen(false)} title="Pause Subscription">
        <p className="text-sm text-gray-600 mb-3">
          Why are you pausing subscription <span className="font-mono">{subscription.id?.slice(-8)}</span>?
        </p>
        <div className="space-y-2 mb-3">
          {PAUSE_REASONS.map(r => (
            <label key={r} className="flex items-center gap-2 cursor-pointer text-sm">
              <input
                type="radio"
                name="pause-reason"
                value={r}
                checked={pauseReason === r}
                onChange={() => setPauseReason(r)}
                className="w-4 h-4 text-brand-600 focus:ring-brand-500"
              />
              <span className="text-gray-700">{r}</span>
            </label>
          ))}
        </div>
        {pauseReason === 'Other' && (
          <textarea
            value={pauseReasonOther}
            onChange={(e) => setPauseReasonOther(e.target.value)}
            placeholder="Describe the reason..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 mb-2"
          />
        )}
        {error && <p className="text-red-500 text-xs mb-2">{error}</p>}
        <div className="flex justify-end gap-3 mt-2">
          <Button variant="secondary" onClick={() => setPausePromptOpen(false)}>Cancel</Button>
          <Button onClick={confirmPause} disabled={pauseLoading}>
            {pauseLoading ? 'Pausing...' : 'Pause Subscription'}
          </Button>
        </div>
      </Modal>
    </>
  );
};
